// src/pages/FacturacionPresupuestos/ConfiguracionFoliosTab.js
import React, { useState, useEffect } from 'react';
import { 
    FaCog, 
    FaSave, 
    FaUniversity, 
    FaHashtag, 
    FaImage, 
    FaSignature, 
    FaUpload, 
    FaTrash, 
    FaCheckCircle,
    FaUserTie,
    FaBolt,
    FaPlus,
    FaCreditCard,
    FaStar
} from 'react-icons/fa';
import { comprimirImagen } from '../../utils/imageCompressor';
import { formatearFolioEjemploConfig } from '../../utils/folioFacturacionUtils';
import './GestionTabs.css';

const inicializarCuentasBancarias = (conf = {}) => {
    if (Array.isArray(conf.cuentasBancarias) && conf.cuentasBancarias.length > 0) {
        return conf.cuentasBancarias;
    }
    return [
        {
            id: 'cb_default_1',
            banco: conf.banco || 'BBVA Bancomer',
            beneficiario: conf.beneficiario || 'Domus Habita Soluciones Residenciales',
            clabe: conf.clabe || '012 680 01548239012 4',
            cuenta: conf.cuenta || '1548239012',
            tarjeta: conf.tarjeta || '',
            esPrincipal: true
        }
    ];
};

const ConfiguracionFoliosTab = ({
    configuracion = {},
    onGuardarConfiguracion,
    guardando = false,
    onSembrarCatalogo
}) => {
    const [config, setConfig] = useState({
        // Folios
        prefijoPresupuesto: configuracion.prefijoPresupuesto || 'DH-PR-',
        consecutivoPresupuesto: configuracion.consecutivoPresupuesto || 32,
        cerosPresupuesto: configuracion.cerosPresupuesto || 3,

        prefijoPago: configuracion.prefijoPago || 'NP-',
        consecutivoPago: configuracion.consecutivoPago || 1,
        cerosPago: configuracion.cerosPago || 3,

        prefijoRemision: configuracion.prefijoRemision || 'REM-',
        consecutivoRemision: configuracion.consecutivoRemision || 1,
        cerosRemision: configuracion.cerosRemision || 3,

        // Cuentas bancarias (lista multibanco y compatibilidad directa)
        cuentasBancarias: inicializarCuentasBancarias(configuracion),
        banco: configuracion.banco || 'BBVA Bancomer',
        beneficiario: configuracion.beneficiario || 'Domus Habita Soluciones Residenciales',
        clabe: configuracion.clabe || '012 680 01548239012 4',
        cuenta: configuracion.cuenta || '1548239012',
        tarjeta: configuracion.tarjeta || '',

        // Emisor por defecto
        emisorNombre: configuracion.emisorNombre || 'Domus Habita',
        emisorSlogan: configuracion.emisorSlogan || 'Soluciones Residenciales & Control de Accesos',
        emisorRfc: configuracion.emisorRfc || 'DHA220101XYZ',
        emisorDireccion: configuracion.emisorDireccion || 'Querétaro, Qro.',
        emisorTelefono: configuracion.emisorTelefono || '442 813 5434',
        emisorEmail: configuracion.emisorEmail || 'contacto@domushabita.mx',

        // Datos Personales para la Firma del Emisor (Punto 5)
        firmanteNombre: configuracion.firmanteNombre || 'Andrés López Martínez',
        firmanteCargo: configuracion.firmanteCargo || 'Dirección Comercial & Proyectos | Domus Habita',
        firmanteTelefono: configuracion.firmanteTelefono || '442 813 5434',
        firmanteEmail: configuracion.firmanteEmail || 'contacto@domushabita.mx',

        // Logos y Firmas (hasta 3 de c/u)
        logos: configuracion.logos || [],
        firmas: configuracion.firmas || []
    });

    // Sincronizar estado local cuando se reciba configuracion desde Firestore
    useEffect(() => {
        if (configuracion && Object.keys(configuracion).length > 0) {
            setConfig(prev => ({
                ...prev,
                ...configuracion,
                cuentasBancarias: inicializarCuentasBancarias(configuracion),
                logos: configuracion.logos || prev.logos || [],
                firmas: configuracion.firmas || prev.firmas || []
            }));
        }
    }, [configuracion]);

    const handleAgregarCuentaBancaria = () => {
        const nueva = {
            id: `cb_${Date.now()}`,
            banco: '',
            beneficiario: config.beneficiario || config.firmanteNombre || 'Domus Habita',
            clabe: '',
            cuenta: '',
            tarjeta: '',
            esPrincipal: (config.cuentasBancarias || []).length === 0
        };
        const actualizadas = [...(config.cuentasBancarias || []), nueva];
        setConfig(prev => ({ ...prev, cuentasBancarias: actualizadas }));
    };

    const handleEliminarCuentaBancaria = (idx) => {
        if ((config.cuentasBancarias || []).length <= 1) {
            alert('Debe mantenerse al menos una cuenta bancaria registrada.');
            return;
        }
        const actualizadas = config.cuentasBancarias.filter((_, i) => i !== idx);
        if (!actualizadas.some(c => c.esPrincipal)) {
            actualizadas[0].esPrincipal = true;
        }
        const principal = actualizadas.find(c => c.esPrincipal) || actualizadas[0];
        setConfig(prev => ({
            ...prev,
            cuentasBancarias: actualizadas,
            banco: principal.banco || '',
            beneficiario: principal.beneficiario || '',
            clabe: principal.clabe || '',
            cuenta: principal.cuenta || '',
            tarjeta: principal.tarjeta || ''
        }));
    };

    const handleMarcarCuentaPrincipal = (idx) => {
        const actualizadas = (config.cuentasBancarias || []).map((c, i) => ({
            ...c,
            esPrincipal: i === idx
        }));
        const principal = actualizadas[idx];
        setConfig(prev => ({
            ...prev,
            cuentasBancarias: actualizadas,
            banco: principal.banco || '',
            beneficiario: principal.beneficiario || '',
            clabe: principal.clabe || '',
            cuenta: principal.cuenta || '',
            tarjeta: principal.tarjeta || ''
        }));
    };

    const handleUpdateCuentaBancaria = (idx, campo, valor) => {
        const actualizadas = [...(config.cuentasBancarias || [])];
        actualizadas[idx] = { ...actualizadas[idx], [campo]: valor };
        const updates = { cuentasBancarias: actualizadas };
        if (actualizadas[idx].esPrincipal || idx === 0) {
            if (campo === 'banco') updates.banco = valor;
            if (campo === 'beneficiario') updates.beneficiario = valor;
            if (campo === 'clabe') updates.clabe = valor;
            if (campo === 'cuenta') updates.cuenta = valor;
            if (campo === 'tarjeta') updates.tarjeta = valor;
        }
        setConfig(prev => ({ ...prev, ...updates }));
    };

    const [guardadoExitoso, setGuardadoExitoso] = useState(false);
    const [subiendoImg, setSubiendoImg] = useState(false);

    const handleUpdate = (field, val) => {
        setConfig(prev => ({ ...prev, [field]: val }));
    };

    const formatearFolioEjemplo = (prefijo, consecutivo, ceros, tipo = 'presupuesto') => {
        return formatearFolioEjemploConfig(prefijo, consecutivo, ceros, tipo);
    };

    // Subir logo con compresión automática en cliente
    const handleSubirLogo = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (config.logos.length >= 3) {
            alert('Solo se pueden almacenar un máximo de 3 logos en la biblioteca.');
            return;
        }

        try {
            setSubiendoImg(true);
            const dataUrlComprimido = await comprimirImagen(file, 500, 250);
            const nuevoLogo = {
                id: Date.now().toString(),
                url: dataUrlComprimido,
                nombre: file.name
            };
            const nuevosLogos = [...config.logos, nuevoLogo];
            const nuevaConfig = { ...config, logos: nuevosLogos };
            setConfig(nuevaConfig);
            // Guardar automáticamente
            try {
                await onGuardarConfiguracion(nuevaConfig);
            } catch (saveErr) {
                console.warn('Aviso al persistir logo:', saveErr);
            }
        } catch (err) {
            console.error('Error al procesar logo:', err);
            alert('Error al procesar la imagen: ' + err.message);
        } finally {
            setSubiendoImg(false);
        }
    };

    // Subir firma con compresión automática en cliente
    const handleSubirFirma = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (config.firmas.length >= 3) {
            alert('Solo se pueden almacenar un máximo de 3 firmas en la biblioteca.');
            return;
        }

        try {
            setSubiendoImg(true);
            const dataUrlComprimido = await comprimirImagen(file, 400, 160);
            const nuevaFirma = {
                id: Date.now().toString(),
                url: dataUrlComprimido,
                nombre: file.name
            };
            const nuevasFirmas = [...config.firmas, nuevaFirma];
            const nuevaConfig = { ...config, firmas: nuevasFirmas };
            setConfig(nuevaConfig);
            // Guardar automáticamente
            try {
                await onGuardarConfiguracion(nuevaConfig);
            } catch (saveErr) {
                console.warn('Aviso al persistir firma:', saveErr);
            }
        } catch (err) {
            console.error('Error al procesar firma:', err);
            alert('Error al procesar la imagen de firma: ' + err.message);
        } finally {
            setSubiendoImg(false);
        }
    };

    const handleEliminarLogo = async (id) => {
        const nuevosLogos = config.logos.filter(l => l.id !== id);
        const nuevaConfig = { ...config, logos: nuevosLogos };
        setConfig(nuevaConfig);
        await onGuardarConfiguracion(nuevaConfig);
    };

    const handleEliminarFirma = async (id) => {
        const nuevasFirmas = config.firmas.filter(f => f.id !== id);
        const nuevaConfig = { ...config, firmas: nuevasFirmas };
        setConfig(nuevaConfig);
        await onGuardarConfiguracion(nuevaConfig);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await onGuardarConfiguracion(config);
            setGuardadoExitoso(true);
            setTimeout(() => setGuardadoExitoso(false), 3500);
        } catch (err) {
            alert('Error al guardar configuración: ' + err.message);
        }
    };

    return (
        <div className="gestion-tab-container">
            {/* Header */}
            <div className="gestion-tab-header">
                <div className="gestion-tab-title-group">
                    <h2>
                        <FaCog style={{ color: '#475569' }} /> Configuración de Folios, Cuentas y Firmas
                    </h2>
                    <p>
                        Personaliza la numeración consecutiva por documento, datos fiscales, bancarios y tus datos personales para firma.
                    </p>
                </div>
                <div className="gestion-tab-actions">
                    <button 
                        type="button" 
                        className="btn-primary-zen"
                        style={{ background: '#475569' }}
                        onClick={handleSubmit}
                        disabled={guardando || subiendoImg}
                    >
                        <FaSave /> {guardando ? 'Guardando...' : subiendoImg ? 'Procesando Imagen...' : 'Guardar Cambios'}
                    </button>
                </div>
            </div>

            {guardadoExitoso && (
                <div style={{ background: '#dcfce7', border: '1px solid #86efac', padding: '12px 18px', borderRadius: '8px', color: '#15803d', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: 600 }}>
                    <FaCheckCircle /> Configuración y elementos guardados correctamente en la plataforma.
                </div>
            )}

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%', boxSizing: 'border-box' }}>
                
                {/* 1. Nomenclatura y Folios (Responsivo al 110% de zoom) */}
                <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '20px', boxSizing: 'border-box', width: '100%' }}>
                    <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#1e293b', margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FaHashtag style={{ color: '#0284c7' }} /> Nomenclatura y Series Consecutivas de Folios
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', width: '100%', boxSizing: 'border-box' }}>
                        {/* Presupuestos */}
                        <div style={{ background: '#f8fafc', padding: '14px', borderRadius: '8px', border: '1px solid #e2e8f0', minWidth: 0, boxSizing: 'border-box' }}>
                            <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#0f172a' }}>Presupuestos</h4>
                            <div className="sendgrid-input-group" style={{ marginBottom: '8px' }}>
                                <label>Prefijo</label>
                                <input 
                                    type="text" 
                                    className="sendgrid-input-control"
                                    value={config.prefijoPresupuesto}
                                    onChange={(e) => handleUpdate('prefijoPresupuesto', e.target.value)}
                                />
                            </div>
                            <div className="sendgrid-input-group" style={{ marginBottom: '8px' }}>
                                <label>Siguiente Consecutivo</label>
                                <input 
                                    type="number" 
                                    className="sendgrid-input-control"
                                    value={config.consecutivoPresupuesto}
                                    onChange={(e) => handleUpdate('consecutivoPresupuesto', Number(e.target.value))}
                                />
                            </div>
                            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '6px' }}>
                                Próximo: <strong>{formatearFolioEjemplo(config.prefijoPresupuesto, config.consecutivoPresupuesto, config.cerosPresupuesto, 'presupuesto')}</strong>
                            </div>
                        </div>

                        {/* Remisiones */}
                        <div style={{ background: '#f8fafc', padding: '14px', borderRadius: '8px', border: '1px solid #e2e8f0', minWidth: 0, boxSizing: 'border-box' }}>
                            <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#0f172a' }}>Remisiones de Cobro</h4>
                            <div className="sendgrid-input-group" style={{ marginBottom: '8px' }}>
                                <label>Prefijo</label>
                                <input 
                                    type="text" 
                                    className="sendgrid-input-control"
                                    value={config.prefijoRemision}
                                    onChange={(e) => handleUpdate('prefijoRemision', e.target.value)}
                                />
                            </div>
                            <div className="sendgrid-input-group" style={{ marginBottom: '8px' }}>
                                <label>Siguiente Consecutivo</label>
                                <input 
                                    type="number" 
                                    className="sendgrid-input-control"
                                    value={config.consecutivoRemision}
                                    onChange={(e) => handleUpdate('consecutivoRemision', Number(e.target.value))}
                                />
                            </div>
                            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '6px' }}>
                                Próximo: <strong>{formatearFolioEjemplo(config.prefijoRemision, config.consecutivoRemision, config.cerosRemision, 'remision')}</strong>
                            </div>
                        </div>

                        {/* Notas de Pago */}
                        <div style={{ background: '#f8fafc', padding: '14px', borderRadius: '8px', border: '1px solid #e2e8f0', minWidth: 0, boxSizing: 'border-box' }}>
                            <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#0f172a' }}>Notas de Pago</h4>
                            <div className="sendgrid-input-group" style={{ marginBottom: '8px' }}>
                                <label>Prefijo</label>
                                <input 
                                    type="text" 
                                    className="sendgrid-input-control"
                                    value={config.prefijoPago}
                                    onChange={(e) => handleUpdate('prefijoPago', e.target.value)}
                                />
                            </div>
                            <div className="sendgrid-input-group" style={{ marginBottom: '8px' }}>
                                <label>Siguiente Consecutivo</label>
                                <input 
                                    type="number" 
                                    className="sendgrid-input-control"
                                    value={config.consecutivoPago}
                                    onChange={(e) => handleUpdate('consecutivoPago', Number(e.target.value))}
                                />
                            </div>
                            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '6px' }}>
                                Próximo: <strong>{formatearFolioEjemplo(config.prefijoPago, config.consecutivoPago, config.cerosPago, 'pago')}</strong>
                            </div>
                        </div>
                    </div>
                    <div style={{ marginTop: '12px', padding: '8px 12px', background: '#f0fdf4', borderRadius: '6px', border: '1px solid #bbf7d0', fontSize: '11.5px', color: '#166534' }}>
                        💡 <strong>Nomenclatura Inteligente e Irrepetible:</strong> Cada documento se emite con la estructura oficial <code>[PREFIJO]-[AÑO]-[INICIALES CLIENTE]-[CONSECUTIVO]</code> (ej. <em>PRE-2026-AVR-036</em>). Al cambiar de categoría en el editor, el documento adoptará automáticamente la serie que le corresponde.
                    </div>
                </div>

                {/* 2. Datos Personales del Firmante Emisor (Punto 5 del usuario) */}
                <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '20px', boxSizing: 'border-box', width: '100%' }}>
                    <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#1e293b', margin: '0 0 6px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FaUserTie style={{ color: '#16a34a' }} /> Datos Personales para la Firma del Emisor
                    </h3>
                    <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 16px 0' }}>
                        Estos datos se colocarán automáticamente en la firma izquierda de todos tus presupuestos, remisiones y notas de pago.
                        (La firma derecha de aceptación del cliente se vinculará automáticamente con el campo <strong>"Atención a:"</strong> de los datos del cliente).
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '14px', width: '100%', boxSizing: 'border-box' }}>
                        <div className="sendgrid-input-group" style={{ minWidth: 0 }}>
                            <label>Nombre Completo del Firmante</label>
                            <input 
                                type="text"
                                className="sendgrid-input-control"
                                value={config.firmanteNombre}
                                onChange={(e) => handleUpdate('firmanteNombre', e.target.value)}
                                placeholder="Ej. Andrés López Martínez"
                            />
                        </div>
                        <div className="sendgrid-input-group" style={{ minWidth: 0 }}>
                            <label>Cargo o Puesto Oficial</label>
                            <input 
                                type="text"
                                className="sendgrid-input-control"
                                value={config.firmanteCargo}
                                onChange={(e) => handleUpdate('firmanteCargo', e.target.value)}
                                placeholder="Ej. Dirección Comercial & Proyectos | Domus Habita"
                            />
                        </div>
                        <div className="sendgrid-input-group" style={{ minWidth: 0 }}>
                            <label>Teléfono Directo</label>
                            <input 
                                type="tel"
                                className="sendgrid-input-control"
                                value={config.firmanteTelefono}
                                onChange={(e) => handleUpdate('firmanteTelefono', e.target.value)}
                                placeholder="442 813 5434"
                            />
                        </div>
                        <div className="sendgrid-input-group" style={{ minWidth: 0 }}>
                            <label>Correo Electrónico Directo</label>
                            <input 
                                type="email"
                                className="sendgrid-input-control"
                                value={config.firmanteEmail}
                                onChange={(e) => handleUpdate('firmanteEmail', e.target.value)}
                                placeholder="contacto@domushabita.mx"
                            />
                        </div>
                    </div>
                </div>

                {/* 3. Cuentas Bancarias Multibanco para Cobranza y Transferencias (Punto 2) */}
                <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '20px', boxSizing: 'border-box', width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
                        <div>
                            <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#1e293b', margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <FaUniversity style={{ color: '#15803d' }} /> Datos Bancarios para Cobranza y Transferencias
                            </h3>
                            <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>
                                Registra tus bancos, CLABEs, cuentas y números de tarjeta para mandarlos llamar rápidamente desde el editor de secciones.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={handleAgregarCuentaBancaria}
                            className="btn-primary-zen"
                            style={{ background: '#16a34a', padding: '7px 14px', fontSize: '12.5px' }}
                            title="Agregar otra cuenta bancaria o tarjeta"
                        >
                            <FaPlus /> Agregar otra Cuenta Bancaria
                        </button>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
                        {(config.cuentasBancarias || []).map((cb, idx) => (
                            <div 
                                key={cb.id || idx} 
                                style={{ 
                                    background: cb.esPrincipal ? '#f0fdf4' : '#f8fafc', 
                                    border: cb.esPrincipal ? '1.5px solid #86efac' : '1px solid #e2e8f0', 
                                    borderRadius: '8px', 
                                    padding: '16px',
                                    position: 'relative'
                                }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <strong style={{ fontSize: '13px', color: '#0f172a' }}>
                                            🏦 Cuenta #{idx + 1}: {cb.banco || 'Sin especificar'}
                                        </strong>
                                        {cb.esPrincipal ? (
                                            <span style={{ fontSize: '11px', background: '#dcfce7', color: '#166534', padding: '2px 8px', borderRadius: '12px', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                <FaStar style={{ color: '#eab308' }} /> Principal Predeterminada
                                            </span>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => handleMarcarCuentaPrincipal(idx)}
                                                style={{ background: 'transparent', border: '1px solid #cbd5e1', color: '#475569', fontSize: '11px', padding: '2px 8px', borderRadius: '12px', cursor: 'pointer' }}
                                                title="Establecer como cuenta por defecto"
                                            >
                                                Marcar como Principal
                                            </button>
                                        )}
                                    </div>
                                    {(config.cuentasBancarias || []).length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => handleEliminarCuentaBancaria(idx)}
                                            style={{ background: 'transparent', border: 'none', color: '#ef4444', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                                            title="Eliminar esta cuenta bancaria"
                                        >
                                            <FaTrash /> Eliminar Cuenta
                                        </button>
                                    )}
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', width: '100%', boxSizing: 'border-box' }}>
                                    <div className="sendgrid-input-group" style={{ minWidth: 0 }}>
                                        <label>Institución Bancaria</label>
                                        <input 
                                            type="text"
                                            className="sendgrid-input-control"
                                            value={cb.banco || ''}
                                            onChange={(e) => handleUpdateCuentaBancaria(idx, 'banco', e.target.value)}
                                            placeholder="Ej. BBVA México, Santander"
                                        />
                                    </div>
                                    <div className="sendgrid-input-group" style={{ minWidth: 0 }}>
                                        <label>Titular / Beneficiario</label>
                                        <input 
                                            type="text"
                                            className="sendgrid-input-control"
                                            value={cb.beneficiario || ''}
                                            onChange={(e) => handleUpdateCuentaBancaria(idx, 'beneficiario', e.target.value)}
                                            placeholder="Razón social o titular de la cuenta"
                                        />
                                    </div>
                                    <div className="sendgrid-input-group" style={{ minWidth: 0 }}>
                                        <label>CLABE Interbancaria (18 dígitos)</label>
                                        <input 
                                            type="text"
                                            maxLength="18"
                                            className="sendgrid-input-control"
                                            value={cb.clabe || ''}
                                            onChange={(e) => handleUpdateCuentaBancaria(idx, 'clabe', e.target.value)}
                                            placeholder="012180015592111397"
                                        />
                                    </div>
                                    <div className="sendgrid-input-group" style={{ minWidth: 0 }}>
                                        <label>Número de Cuenta</label>
                                        <input 
                                            type="text"
                                            className="sendgrid-input-control"
                                            value={cb.cuenta || ''}
                                            onChange={(e) => handleUpdateCuentaBancaria(idx, 'cuenta', e.target.value)}
                                            placeholder="1559211139"
                                        />
                                    </div>
                                    <div className="sendgrid-input-group" style={{ minWidth: 0 }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <FaCreditCard style={{ color: '#0284c7' }} /> Número de Tarjeta (16 dígitos)
                                        </label>
                                        <input 
                                            type="text"
                                            maxLength="19"
                                            className="sendgrid-input-control"
                                            value={cb.tarjeta || ''}
                                            onChange={(e) => handleUpdateCuentaBancaria(idx, 'tarjeta', e.target.value)}
                                            placeholder="4152 3134 5678 9012"
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* 4. Logos y Firmas Digitales (Con compresión y guardado garantizado) */}
                <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '20px', boxSizing: 'border-box', width: '100%' }}>
                    <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#1e293b', margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FaImage style={{ color: '#d97706' }} /> Biblioteca de Logos y Firmas Digitales (Hasta 3 de cada uno)
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', width: '100%', boxSizing: 'border-box' }}>
                        {/* Logos */}
                        <div style={{ minWidth: 0 }}>
                            <h4 style={{ fontSize: '13px', margin: '0 0 10px 0', color: '#334155' }}>Logotipos Registrados ({config.logos.length}/3)</h4>
                            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                {config.logos.map((logo) => (
                                    <div key={logo.id} style={{ position: 'relative', width: '110px', height: '80px', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '6px', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <img src={logo.url} alt={logo.nombre} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                        <button 
                                            type="button" 
                                            onClick={() => handleEliminarLogo(logo.id)}
                                            style={{ position: 'absolute', top: '-6px', right: '-6px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '50%', width: '22px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '11px', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }}
                                            title="Eliminar logo"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ))}
                                {config.logos.length < 3 && (
                                    <label style={{ width: '110px', height: '80px', border: '1.5px dashed #cbd5e1', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: '#fafbfc', transition: 'all 0.15s ease' }}>
                                        <FaUpload style={{ color: '#94a3b8', marginBottom: '4px' }} />
                                        <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600 }}>Subir Logo</span>
                                        <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleSubirLogo} disabled={subiendoImg} />
                                    </label>
                                )}
                            </div>
                            <span style={{ fontSize: '11px', color: '#94a3b8', display: 'block', marginTop: '8px' }}>
                                * Las imágenes se optimizan y guardan automáticamente en la nube.
                            </span>
                        </div>

                        {/* Firmas */}
                        <div style={{ minWidth: 0 }}>
                            <h4 style={{ fontSize: '13px', margin: '0 0 10px 0', color: '#334155' }}>Firmas y Sellos Digitales ({config.firmas.length}/3)</h4>
                            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                {config.firmas.map((firma) => (
                                    <div key={firma.id} style={{ position: 'relative', width: '110px', height: '80px', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '6px', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <img src={firma.url} alt={firma.nombre} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                        <button 
                                            type="button" 
                                            onClick={() => handleEliminarFirma(firma.id)}
                                            style={{ position: 'absolute', top: '-6px', right: '-6px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '50%', width: '22px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '11px', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }}
                                            title="Eliminar firma"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ))}
                                {config.firmas.length < 3 && (
                                    <label style={{ width: '110px', height: '80px', border: '1.5px dashed #cbd5e1', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: '#fafbfc', transition: 'all 0.15s ease' }}>
                                        <FaSignature style={{ color: '#94a3b8', marginBottom: '4px' }} />
                                        <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600 }}>Subir Firma</span>
                                        <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleSubirFirma} disabled={subiendoImg} />
                                    </label>
                                )}
                            </div>
                            <span style={{ fontSize: '11px', color: '#94a3b8', display: 'block', marginTop: '8px' }}>
                                * Soporta fondos transparentes PNG para firmas y sellos.
                            </span>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default ConfiguracionFoliosTab;
